import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import Header from 'components/ui/Header';
import Button from 'components/ui/Button';


import Icon from 'components/AppIcon';
import ServiceDescriptionSection from './components/ServiceDescriptionSection';
import LocationSection from './components/LocationSection';
import TimingSection from './components/TimingSection';
import MediaUploadSection from './components/MediaUploadSection';
import { serviceRequestService } from 'services/serviceRequestService';
import { checkDuplicateServiceType } from 'services/serviceRequestService';
import { supabase } from 'lib/supabase';
import Footer from 'pages/landing-page/components/Footer';

const NewServiceRequestPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);

  // Get selected profession from navigation state
  const selectedProfession = location?.state?.selectedProfession;

  // All known profession names (for missing-category detection)
  const [allProfessionNames, setAllProfessionNames] = useState([]);

  // Form state
  const [formData, setFormData] = useState({
    serviceType: selectedProfession?.name || '',
    title: '',
    description: '',
    locationMapboxId: '',
    locationName: '',
    urgency: 'medium',
    preferredDate: '',
    preferredTimeSlot: '',
    additionalDetails: ''
  });

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [duplicateWarning, setDuplicateWarning] = useState(false);
  const [targetProfessional, setTargetProfessional] = useState(null);
  const [targetProfessionalCategories, setTargetProfessionalCategories] = useState([]);

  // Installments feature flag from platform_config
  const [installmentsEnabled, setInstallmentsEnabled] = useState(false);
  const [installmentsRequested, setInstallmentsRequested] = useState(false);
  const [installmentsLabel, setInstallmentsLabel] = useState('Richiedi pagamento rateale');
  const [installmentsHelper, setInstallmentsHelper] = useState("Indica ai professionisti che sei interessato a un pagamento a rate. Saranno loro a proporti l'eventuale soluzione nel preventivo.");

  // Auth state for unauthenticated users
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState(null);
  const [loginLoading, setLoginLoading] = useState(false);

  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regPasswordConfirm, setRegPasswordConfirm] = useState('');
  const [regTerms, setRegTerms] = useState(false);
  const [regError, setRegError] = useState(null);
  const [regLoading, setRegLoading] = useState(false);
  const [pendingConfirmation, setPendingConfirmation] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');
  const [resendConfirmed, setResendConfirmed] = useState(false);

  // Fetch all profession names once on mount (for missing-category detection)
  useEffect(() => {
    (async () => {
      try {
        const { data } = await supabase?.from('professions')?.select('name');
        if (data) {
          setAllProfessionNames((data || [])?.map(r => r?.name?.toLowerCase()?.trim()));
        }
      } catch (_) {}
    })();
  }, []);

  // Fetch installments_enabled flag from platform_config
  useEffect(() => {
    (async () => {
      try {
        const { data } = await supabase
          ?.from('platform_config')
          ?.select('value')
          ?.eq('key', 'installments_enabled')
          ?.single();
        if (data?.value === true) {
          setInstallmentsEnabled(true);
        }
      } catch (_) {}

      try {
        const { data: textData } = await supabase
          ?.from('platform_config')
          ?.select('value')
          ?.eq('key', 'installments_client_text')
          ?.single();
        if (textData?.value?.title) {
          setInstallmentsLabel(textData?.value?.title);
        }
        if (textData?.value?.subtitle) {
          setInstallmentsHelper(textData?.value?.subtitle);
        }
      } catch (_) {}
    })();
  }, []);

  // Silently insert into category_requests if serviceType doesn't match any known profession
  const maybeCaptureUnknownCategory = async (serviceType) => {
    if (!serviceType?.trim()) return;
    const normalized = serviceType?.toLowerCase()?.trim();
    const isKnown = allProfessionNames?.some(n => n === normalized);
    if (isKnown) return;

    try {
      const { error } = await supabase?.from('category_requests')?.insert({
        requested_text: serviceType?.trim(),
        context: 'nuova_richiesta',
      });
      if (error) {
        console.error('❌ category_requests insert error:', error);
      }
    } catch (err) {
      console.error('❌ category_requests insert exception:', err);
    }
  };

  // Read ?pro= from URL to pre-target a specific professional
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const proId = params?.get('pro');
    const proName = params?.get('proName');
    if (proId) {
      setTargetProfessional({ id: proId, name: proName || 'Professionista' });

      // Fetch professional's categories from professional_professions
      (async () => {
        const { data: profRows } = await supabase?.from('professional_professions')?.select('professions(name)')?.eq('user_id', proId);

        const professions = (profRows || [])?.map(r => r?.professions?.name)?.filter(Boolean);
        setTargetProfessionalCategories(professions);

        // Also enrich targetProfessional with categories
        setTargetProfessional({ id: proId, name: proName || 'Professionista', categories: professions });

        // Auto-set serviceType if exactly one category
        if (professions?.length === 1) {
          setFormData(prev => ({ ...prev, serviceType: professions?.[0] }));
        }
      })();
    } else {
      // Fallback to sessionStorage (post-registration redirect)
      try {
        const pending = sessionStorage.getItem('volaptic_pending_request_pro');
        if (pending) {
          const obj = JSON.parse(pending);
          if (obj?.professional_id) {
            setTargetProfessional({ id: obj?.professional_id, name: obj?.professional_name || 'Professionista' });
          }
        }
      } catch (_) {}
    }
  }, []);

  // Redirect if not coming from dashboard or not authenticated
  useEffect(() => {
    const checkAccess = async () => {
      if (authLoading) return;

      // Step 1: If ?pro= is present, allow unauthenticated access — skip all redirects
      const proId = new URLSearchParams(window.location.search)?.get('pro');
      if (proId) {
        setLoading(false);
        return;
      }

      if (!user) {
        if (!selectedProfession) {
          // No profession in state and not logged in → send to login
          navigate('/login-form?returnTo=/cliente/dashboard');
          return;
        }
        // Has selectedProfession but not logged in → allow access
        // The page renders login+register blocks below the form
        setLoading(false);
        return;
      }

      // Check if user came from dashboard with service selection
      if (!selectedProfession) {
        console.log('⚠️ Direct access blocked - redirecting to dashboard');
        navigate('/cliente/dashboard');
        return;
      }

      // Pre-fill default_city from users table
      try {
        const { data: userData } = await supabase
          ?.from('users')
          ?.select('default_city')
          ?.eq('id', user?.id)
          ?.single();
        if (userData?.default_city) {
          setFormData(prev => ({ ...prev, locationName: userData?.default_city }));
        }
      } catch (err) {
        // silently ignore — field stays blank
      }

      setLoading(false);
    };

    checkAccess();
  }, [user, authLoading, navigate, selectedProfession]);

  // Validate form
  useEffect(() => {
    const validateForm = () => {
      const requiredFields = ['serviceType', 'title', 'description', 'locationName'];
      const isValid = requiredFields?.every(field => formData?.[field]?.toString()?.trim() !== '');
      setIsFormValid(isValid);
    };

    validateForm();
  }, [formData]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error for this field
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleFilesChange = (files) => {
    setSelectedFiles(files);
  };

  const handleBack = () => {
    navigate('/cliente/dashboard');
  };

  const handleContinue = async () => {
    if (!isFormValid || isSubmitting) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Check for duplicate service type before creating (only for authenticated users)
      if (user) {
        const { hasDuplicate } = await checkDuplicateServiceType(formData?.serviceType, user?.id);

        if (hasDuplicate) {
          setDuplicateWarning(true);
          setIsSubmitting(false);
          return;
        }
      }

      // Silently capture unknown category (non-blocking, before creating request)
      await maybeCaptureUnknownCategory(formData?.serviceType);

      await doCreateRequest();
    } catch (error) {
      console.error('❌ Error creating service request:', error);
      setSubmitError(`Errore: ${error?.message || 'Si è verificato un errore. Riprova.'}`);
      setIsSubmitting(false);
    }
  };

  const doCreateRequest = async () => {
    setIsSubmitting(true);
    setSubmitError(null);
    setDuplicateWarning(false);

    try {
      // Extract location mapbox_id from locationData if available
      const locationMapboxId = formData?.locationData?.mapbox_id || formData?.locationMapboxId || '';

      // Prepare request data
      const requestData = {
        serviceType: formData?.serviceType,
        title: formData?.title,
        description: formData?.description,
        locationMapboxId: locationMapboxId,
        locationName: formData?.locationName,
        urgency: formData?.urgency,
        preferredDate: formData?.preferredDate || null,
        preferredTimeSlot: formData?.preferredTimeSlot || null,
        additionalDetails: formData?.additionalDetails || null,
        source: targetProfessional?.id ? 'professional_landing' : 'marketplace',
        sourceSlug: targetProfessional?.slug || null,
        installmentsRequested: installmentsEnabled ? installmentsRequested : false,
      };

      if (targetProfessional?.id) {
        requestData.assignedProfessionalId = targetProfessional?.id;
      }

      // Create service request in Supabase (awaited)
      const result = await serviceRequestService?.createServiceRequest(requestData);

      // Check if database insert was successful
      if (result?.success) {
        console.log('✅ Service request created:', result?.data);
        const newRequestId = result?.data?.id;

        // Save locationName back to users.default_city (non-blocking, only for authenticated users)
        if (user && formData?.locationName?.trim()) {
          supabase
            ?.from('users')
            ?.update({ default_city: formData?.locationName?.trim() })
            ?.eq('id', user?.id)
            ?.then(() => {})
            ?.catch(() => {});
        }

        // Call match-request edge function (non-blocking)
        supabase?.functions?.invoke('match-request', {
          body: { request_id: newRequestId }
        })?.then(({ data }) => {
          console.log('✅ match-request result:', data);
        })?.catch((err) => {
          console.error('⚠️ match-request failed (non-blocking):', err);
        });

        // Upload media files if any were selected
        if (selectedFiles?.length > 0) {
          console.log(`📤 Uploading ${selectedFiles?.length} media files...`);
          const uploadResult = await serviceRequestService?.uploadServiceRequestMedia(
            result?.data?.id,
            selectedFiles
          );
          
          if (uploadResult?.success) {
            console.log('✅ Media files uploaded successfully');
            if (uploadResult?.partialErrors) {
              console.warn('⚠️ Some files failed to upload:', uploadResult?.partialErrors);
            }
          } else {
            console.error('❌ Media upload failed:', uploadResult?.error);
            // Continue to dashboard even if media upload fails
          }
        }
        
        // Redirect to dashboard after successful creation
        navigate('/cliente/dashboard');
      } else {
        // Database insert failed - log and surface the error
        const errorMessage = result?.error || 'Errore durante la creazione della richiesta';
        console.error('❌ Database insert failed:', errorMessage);
        throw new Error(errorMessage);
      }
    } catch (error) {
      // Log and surface any errors
      console.error('❌ Error creating service request:', error);
      setSubmitError(`Errore: ${error?.message || 'Si è verificato un errore. Riprova.'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLoginAndSubmit = async () => {
    setLoginLoading(true);
    setLoginError(null);
    const { error } = await supabase?.auth?.signInWithPassword({ email: loginEmail, password: loginPassword });
    if (error) {
      setLoginError('Credenziali non valide. Controlla email e password.');
      setLoginLoading(false);
      return;
    }
    await new Promise(r => setTimeout(r, 400));
    await doCreateRequest();
    setLoginLoading(false);
  };

  const handleRegisterAndDefer = async () => {
    setRegError(null);

    // Email format check
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!regEmail || !emailRegex?.test(regEmail?.trim())) {
      setRegError('Inserisci un indirizzo email valido.');
      return;
    }

    // Password length
    if (!regPassword || regPassword?.length < 8) {
      setRegError('La password deve essere di almeno 8 caratteri.');
      return;
    }

    // Password match
    if (regPassword !== regPasswordConfirm) {
      setRegError('Le due password non coincidono. Controlla e riprova.');
      return;
    }

    // Terms accepted
    if (!regTerms) {
      setRegError('Devi accettare i Termini e Condizioni e la Privacy Policy per registrarti.');
      return;
    }

    setRegLoading(true);

    const pendingRequest = {
      serviceType: formData?.serviceType,
      title: formData?.title,
      description: formData?.description,
      locationMapboxId: formData?.locationData?.mapbox_id || formData?.locationMapboxId || '',
      locationName: formData?.locationName,
      urgency: formData?.urgency || 'medium',
      preferredDate: formData?.preferredDate || null,
      preferredTimeSlot: formData?.preferredTimeSlot || null,
      additionalDetails: formData?.additionalDetails || null,
      assignedProfessionalId: targetProfessional?.id || null,
      source: targetProfessional?.id ? 'professional_landing' : 'marketplace',
      sourceSlug: targetProfessional?.slug || null,
    };

    const { error } = await supabase?.auth?.signUp({
      email: regEmail,
      password: regPassword,
      options: {
        data: {
          role: 'cliente',
          email_confirmed: false,
          registration_step: 1,
          pending_request: pendingRequest,
        },
        emailRedirectTo: `${window.location?.origin}/auth/callback?role=cliente`,
      },
    });

    if (error) {
      const msg = (error?.message || '')?.toLowerCase();
      let humanMessage = 'Errore durante la registrazione. Riprova.';
      if (msg?.includes('already registered') || msg?.includes('user already')) {
        humanMessage = 'Questa email è già registrata. Usa il blocco "Hai già un account?" qui sopra per accedere.';
      } else if (msg?.includes('password') && msg?.includes('weak')) {
        humanMessage = 'La password è troppo debole. Usa almeno 8 caratteri con lettere e numeri.';
      } else if (msg?.includes('rate') || msg?.includes('too many')) {
        humanMessage = 'Troppi tentativi. Aspetta qualche minuto e riprova.';
      } else if (msg?.includes('invalid email')) {
        humanMessage = 'Indirizzo email non valido.';
      } else if (error?.message) {
        humanMessage = `Errore: ${error?.message}`;
      }
      setRegError(humanMessage);
      setRegLoading(false);
      return;
    }

    setPendingEmail(regEmail);
    setPendingConfirmation(true);
    setRegLoading(false);
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-600">Caricamento...</p>
        </div>
      </div>
    );
  }

  // Pending confirmation screen
  if (pendingConfirmation) {
    return (
      <div className="flex flex-col min-h-screen">
        <div
          className="flex-1"
          style={{
            backgroundImage: 'url(/assets/images/italian_home_interior_bg.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: 'fixed',
          }}
        >
          <div style={{ backgroundColor: 'rgba(255,255,255,0.55)' }}>
            <Header />
            <div className="max-w-lg mx-auto px-4 py-16 sm:px-6" style={{ textAlign: 'center' }}>
              <div style={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '16px', padding: '48px 32px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '24px' }}>
                  <Icon name="Mail" size={56} style={{ color: '#104d98' }} />
                </div>
                <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#111827', marginBottom: '16px' }}>
                  Controlla la tua email
                </h1>
                <p style={{ fontSize: '15px', color: '#374151', lineHeight: '1.6', marginBottom: '24px' }}>
                  Abbiamo inviato un link di conferma a <strong>{pendingEmail}</strong>. Clicca il link per attivare il tuo account: la tua richiesta verrà inviata a <strong>{targetProfessional?.name || 'il professionista'}</strong> in automatico.
                </p>
                <div>
                  {resendConfirmed ? (
                    <p style={{ fontSize: '13px', color: '#16a34a' }}>Email inviata ✓</p>
                  ) : (
                    <button
                      type="button"
                      onClick={async () => {
                        await supabase?.auth?.resend({ type: 'signup', email: pendingEmail });
                        setResendConfirmed(true);
                        setTimeout(() => setResendConfirmed(false), 4000);
                      }}
                      style={{ fontSize: '13px', color: '#104d98', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
                    >
                      Non hai ricevuto l'email? Reinvia
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div style={{ backgroundColor: '#ffffff' }}>
          <Footer />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      {/* Background image area — does NOT extend into footer */}
      <div
        className="flex-1"
        style={{
          backgroundImage: 'url(/assets/images/italian_home_interior_bg.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
        }}
      >
        {/* White overlay */}
        <div
          style={{ backgroundColor: 'rgba(255,255,255,0.55)' }}
        >
          <Header />

          <div className="max-w-3xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
            {/* Breadcrumb */}
            <div className="mb-6">
              <button
                onClick={handleBack}
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name="ArrowLeft" size={16} />
                Torna alla Dashboard
              </button>
            </div>

            {/* Page Header */}
            <div className="mt-8 mb-6">
              <h1 className="text-3xl font-bold text-foreground mb-1">Nuova Richiesta</h1>
              <p className="text-base text-muted-foreground mb-2">Confronta i migliori professionisti della tua zona</p>
              {targetProfessional ? (
                <p className="text-muted-foreground">
                  Richiesta esclusiva per <span className="font-semibold text-foreground">{targetProfessional?.name}</span> · Servizio: <span className="font-semibold text-foreground">{formData?.serviceType || '—'}</span>
                </p>
              ) : (
                <p className="text-muted-foreground">
                  Servizio selezionato: <span className="font-semibold text-foreground">{formData?.serviceType}</span>
                </p>
              )}
            </div>

            {/* Form Sections */}
            <div className="space-y-6">
              {targetProfessional && (
                <div style={{ backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderLeft: '4px solid #2563eb', borderRadius: '8px', padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                  <span style={{ fontSize: '14px', color: '#1e40af' }}>
                    Stai inviando una richiesta diretta a <strong>{targetProfessional?.name}</strong>. Solo questo professionista riceverà il tuo preventivo.
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setTargetProfessional(null);
                      setTargetProfessionalCategories([]);
                      try { sessionStorage.removeItem('volaptic_pending_request_pro'); } catch(_){}
                      const url = new URL(window.location.href);
                      url?.searchParams?.delete('pro');
                      url?.searchParams?.delete('proName');
                      window.history?.replaceState(null, '', url?.pathname);
                    }}
                    style={{ flexShrink: 0, fontSize: '13px', color: '#1d4ed8', background: 'none', border: '1px solid #93c5fd', borderRadius: '6px', padding: '4px 10px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                  >
                    Rimuovi
                  </button>
                </div>
              )}

              {/* Step 3: Category selector — shown when pro has multiple categories and serviceType not yet chosen */}
              {targetProfessional && targetProfessionalCategories?.length > 1 && !formData?.serviceType && (
                <div style={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px' }}>
                  <p style={{ fontSize: '15px', fontWeight: '600', color: '#111827', marginBottom: '12px' }}>
                    Per quale servizio vuoi contattare {targetProfessional?.name}?
                  </p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    {targetProfessionalCategories?.map((cat) => (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, serviceType: cat }))}
                        style={{ padding: '8px 18px', fontSize: '14px', fontWeight: '500', color: '#1d4ed8', backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', cursor: 'pointer' }}
                      >
                        {cat}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <ServiceDescriptionSection
                title={formData?.title}
                description={formData?.description}
                additionalDetails={formData?.additionalDetails}
                onChange={handleChange}
                errors={errors}
              />

              <LocationSection
                locationName={formData?.locationName}
                onChange={handleChange}
                errors={errors}
              />

              <TimingSection
                urgency={formData?.urgency}
                preferredDate={formData?.preferredDate}
                preferredTimeSlot={formData?.preferredTimeSlot}
                onChange={handleChange}
                errors={errors}
              />

              <MediaUploadSection onFilesChange={handleFilesChange} />
            </div>

            {/* Installments checkbox — shown only when platform_config installments_enabled = true */}
            {installmentsEnabled && user && (
              <div style={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px', marginTop: '24px' }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={installmentsRequested}
                    onChange={e => setInstallmentsRequested(e?.target?.checked)}
                    style={{ marginTop: '2px', flexShrink: 0, width: '16px', height: '16px', cursor: 'pointer', accentColor: '#104d98' }}
                  />
                  <div>
                    <span style={{ fontSize: '15px', fontWeight: '600', color: '#111827', display: 'block' }}>
                      {installmentsLabel}
                    </span>
                    <span style={{ fontSize: '13px', color: '#6b7280', display: 'block', marginTop: '4px', lineHeight: '1.5' }}>
                      {installmentsHelper}
                    </span>
                  </div>
                </label>
              </div>
            )}

            {/* Action Buttons — only shown when authenticated */}
            {user && (
              <div className="mt-8 flex gap-4">
                <Button
                  variant="outline"
                  size="lg"
                  onClick={handleBack}
                  iconName="ArrowLeft"
                  className="flex-1"
                >
                  Indietro
                </Button>
                <Button
                  variant="default"
                  size="lg"
                  onClick={handleContinue}
                  disabled={!isFormValid || isSubmitting}
                  iconName="ArrowRight"
                  iconPosition="right"
                  className="flex-1"
                >
                  {isSubmitting ? 'Invio in corso...' : 'Continua'}
                </Button>
              </div>
            )}

            {/* Duplicate Warning Box */}
            {duplicateWarning && (
              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-300 rounded-lg">
                <div className="flex items-start gap-3">
                  <Icon name="AlertTriangle" size={20} className="text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-yellow-800">
                      Hai già una richiesta attiva per {formData?.serviceType}. Vuoi aprirne un'altra comunque?
                    </p>
                    <div className="flex gap-3 mt-3">
                      <button
                        onClick={doCreateRequest}
                        className="px-4 py-2 text-sm font-medium bg-yellow-600 text-white rounded-md hover:bg-yellow-700 transition-colors"
                      >
                        Sì, procedi
                      </button>
                      <button
                        onClick={() => setDuplicateWarning(false)}
                        className="px-4 py-2 text-sm font-medium bg-white text-yellow-800 border border-yellow-300 rounded-md hover:bg-yellow-50 transition-colors"
                      >
                        Annulla
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Error Message Display */}
            {submitError && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <Icon name="AlertCircle" size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-red-800">Errore</p>
                    <p className="text-sm text-red-700 mt-1">{submitError}</p>
                  </div>
                </div>
              </div>
            )}

            {user && !isFormValid && (
              <p className="mt-3 text-sm text-gray-600 text-center">
                Completa tutti i campi obbligatori per continuare
              </p>
            )}

            {/* Inline Auth Sections — shown only when NOT authenticated */}
            {!user && (
              <div className="mt-8">
                {/* Card A — Login */}
                <div style={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '24px', marginBottom: '16px', boxShadow: '0 1px 6px rgba(0,0,0,0.06)' }}>
                  <h2 style={{ fontSize: '16px', fontWeight: '700', color: '#111827', marginBottom: '16px' }}>
                    Hai già un account?
                  </h2>
                  <div style={{ marginBottom: '12px' }}>
                    <input
                      type="email"
                      placeholder="tuo@email.it"
                      value={loginEmail}
                      onChange={e => setLoginEmail(e?.target?.value)}
                      style={{ width: '100%', padding: '10px 12px', fontSize: '14px', border: '1px solid #d1d5db', borderRadius: '8px', outline: 'none', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <input
                      type="password"
                      placeholder="La tua password"
                      value={loginPassword}
                      onChange={e => setLoginPassword(e?.target?.value)}
                      style={{ width: '100%', padding: '10px 12px', fontSize: '14px', border: '1px solid #d1d5db', borderRadius: '8px', outline: 'none', boxSizing: 'border-box' }}
                    />
                  </div>
                  {loginError && (
                    <p style={{ fontSize: '13px', color: '#dc2626', marginBottom: '12px' }}>{loginError}</p>
                  )}
                  <button
                    type="button"
                    onClick={handleLoginAndSubmit}
                    disabled={loginLoading}
                    style={{ width: '100%', backgroundColor: loginLoading ? '#6b7280' : '#104d98', color: '#ffffff', borderRadius: '8px', padding: '12px', fontWeight: '600', fontSize: '15px', border: 'none', cursor: loginLoading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                  >
                    {loginLoading && (
                      <span style={{ width: '16px', height: '16px', border: '2px solid #ffffff', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />
                    )}
                    Accedi e invia la richiesta
                  </button>
                </div>

                {/* Card B — Register */}
                <div style={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '24px', marginBottom: '16px', boxShadow: '0 1px 6px rgba(0,0,0,0.06)' }}>
                  <h2 style={{ fontSize: '16px', fontWeight: '700', color: '#111827', marginBottom: '4px' }}>
                    Non sei ancora registrato?
                  </h2>
                  <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px' }}>
                    Crea un account in 30 secondi
                  </p>
                  <div style={{ marginBottom: '12px' }}>
                    <input
                      type="email"
                      placeholder="tuo@email.it"
                      value={regEmail}
                      onChange={e => setRegEmail(e?.target?.value)}
                      style={{ width: '100%', padding: '10px 12px', fontSize: '14px', border: '1px solid #d1d5db', borderRadius: '8px', outline: 'none', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <input
                      type="password"
                      placeholder="Minimo 8 caratteri"
                      value={regPassword}
                      onChange={e => setRegPassword(e?.target?.value)}
                      style={{ width: '100%', padding: '10px 12px', fontSize: '14px', border: '1px solid #d1d5db', borderRadius: '8px', outline: 'none', boxSizing: 'border-box' }}
                    />
                    {regPassword === '' ? (
                      <span style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>Almeno 8 caratteri</span>
                    ) : regPassword?.length < 8 ? (
                      <span style={{ display: 'block', fontSize: '12px', color: '#dc2626', marginTop: '4px' }}>
                        Ancora {8 - regPassword?.length} {8 - regPassword?.length === 1 ? 'carattere' : 'caratteri'}
                      </span>
                    ) : (
                      <span style={{ display: 'block', fontSize: '12px', color: '#16a34a', marginTop: '4px' }}>Password valida ✓</span>
                    )}
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <input
                      type="password"
                      placeholder="Ripeti la password"
                      value={regPasswordConfirm}
                      onChange={e => setRegPasswordConfirm(e?.target?.value)}
                      style={{ width: '100%', padding: '10px 12px', fontSize: '14px', border: '1px solid #d1d5db', borderRadius: '8px', outline: 'none', boxSizing: 'border-box' }}
                    />
                    {regPasswordConfirm !== '' && (
                      regPassword === regPasswordConfirm ? (
                        <span style={{ display: 'block', fontSize: '12px', color: '#16a34a', marginTop: '4px' }}>Le password coincidono ✓</span>
                      ) : (
                        <span style={{ display: 'block', fontSize: '12px', color: '#dc2626', marginTop: '4px' }}>Le password non coincidono</span>
                      )
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '12px' }}>
                    <input
                      type="checkbox"
                      id="regTermsCheck"
                      checked={regTerms}
                      onChange={e => setRegTerms(e?.target?.checked)}
                      style={{ marginTop: '2px', flexShrink: 0, cursor: 'pointer' }}
                    />
                    <label htmlFor="regTermsCheck" style={{ fontSize: '13px', color: '#374151', cursor: 'pointer', lineHeight: '1.5' }}>
                      Accetto i{' '}
                      <a href="/terms-and-conditions" target="_blank" style={{ color: '#104d98', textDecoration: 'underline' }}>Termini e Condizioni</a>
                      {' '}e la{' '}
                      <a href="/privacy-policy" target="_blank" style={{ color: '#104d98', textDecoration: 'underline' }}>Privacy Policy</a>
                    </label>
                  </div>
                  {regError && (
                    <p style={{ fontSize: '13px', color: '#dc2626', marginBottom: '12px' }}>{regError}</p>
                  )}
                  <button
                    type="button"
                    onClick={handleRegisterAndDefer}
                    disabled={regLoading || !regTerms}
                    style={{ width: '100%', backgroundColor: (regLoading || !regTerms) ? '#6b7280' : '#104d98', color: '#ffffff', borderRadius: '8px', padding: '12px', fontWeight: '600', fontSize: '15px', border: 'none', cursor: (regLoading || !regTerms) ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                  >
                    {regLoading && (
                      <span style={{ width: '16px', height: '16px', border: '2px solid #ffffff', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />
                    )}
                    Registrati e invia la richiesta
                  </button>
                  <p style={{ fontSize: '13px', color: '#6b7280', marginTop: '12px', textAlign: 'center' }}>
                    La richiesta verrà inviata al professionista appena attivi il tuo account.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Footer with its own solid white background — fully separate from the bg image area */}
      <div style={{ backgroundColor: '#ffffff' }}>
        <Footer />
      </div>
    </div>
  );
};

export default NewServiceRequestPage;